import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'

const bankTransactionsSearchSchema = z.object({
  page: z.number().int().positive().catch(1),
  search: z.union([z.string(), z.number()]).transform(val => String(val)).optional(),
  reconciliation_status: z.string().optional(),
  direction: z.string().optional(),
})



import { useState, useEffect } from 'react';
import { apiFetch } from '@/utils/api';
import { useI18n } from '@/hooks/useI18n';
import Layout from '../../../components/Layout';
import { CurrentUserProvider } from '../../../hooks/useCurrentUser';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowUp, ArrowDown, ArrowUpDown, Search } from 'lucide-react';
import { formatCurrency, formatDate } from '@/utils/formatters';
import {TagFilter} from '@/components/TagFilter';
import PaymentAdvancedFilter from '@/components/PaymentAdvancedFilter';
import PaginationFooter from '../../../components/PaginationFooter';
import BankReconcileModal from '../../../components/BankReconcileModal';
import { showSuccessToast, showErrorToast } from '@/hooks/useToast';
import MultipleSelector, { Option } from '@/components/ui/multiple-selector';

interface PaymentInstrument {
    id: string;
    name: string;
    account_number?: string;
}

interface BankTransaction {
    id: string;
    payment_instrument?: PaymentInstrument;
    direction?: string;
    amount: number;
    currency: string;
    transaction_date: string;
    transaction_type?: string;
    invoice?: { invoice_number?: string } | null;
    invoices?: { id: string; invoice_number?: string }[] | null;
    bank_reference?: string | null;
    description?: string | null;
    reconciliation_status: string;
    balance_after?: number;
    created_at: string;
}

interface Filters {
    reconciliation_status: string;
    direction: string;
    transaction_types: string[];
    search: string;
}

interface AdvancedFilters {
    cost_center_id?: string;
    payment_instrument_id?: string;
    date_from?: string;
    date_to?: string;
    currency?: string;
    min_amount?: string;
    max_amount?: string;
}

interface Pagination {
    page: number;
    total: number;
    pages: number;
    limit: number;
}

const reconciliationStatusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'unreconciled', label: 'Unreconciled' },
    { value: 'reconciled', label: 'Reconciled' },
    { value: 'ignored', label: 'Ignored' },
];

const directionOptions = [
    { value: 'all', label: 'All Directions' },
    { value: 'incoming', label: 'Incoming (Positive)' },
    { value: 'outgoing', label: 'Outgoing (Negative)' },
];

const transactionTypeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'ÁTUTALÁS', label: 'ÁTUTALÁS' },
    { value: 'KÁRTYATRANZAKCIÓ', label: 'KÁRTYATRANZAKCIÓ' },
    { value: 'DÍJ, KAMAT', label: 'DÍJ, KAMAT' },
    { value: 'EGYÉB TERHELÉS', label: 'EGYÉB TERHELÉS' },
    { value: 'EGYÉB JÓVÁÍRÁS', label: 'EGYÉB JÓVÁÍRÁS' },
];

const getReconciliationStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
        case 'reconciled':
            return 'default';
        case 'unreconciled':
            return 'secondary';
        case 'ignored':
            return 'outline';
        default:
            return 'outline';
    }
};

export default function BankTransactionsPage() {
    const { t } = useI18n();
    const [bankTransactions, setBankTransactions] = useState<BankTransaction[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [reconciling, setReconciling] = useState<boolean>(false);
    const [filters, setFilters] = useState<Filters>({
        reconciliation_status: '',
        direction: '',
        transaction_types: [],
        search: '',
    });
    const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>({});
    const [pagination, setPagination] = useState<Pagination>({
        page: 1,
        total: 0,
        pages: 0,
        limit: 50,
    });
    const [sortBy, setSortBy] = useState<string>('transaction_date');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
    const [tagFilter, setTagFilter] = useState<{tagIds: string[], matchMode: 'AND' | 'OR'} | null>(null);

    // Modal state
    const [reconcileModalOpen, setReconcileModalOpen] = useState<boolean>(false);
    const [selectedTransaction, setSelectedTransaction] = useState<BankTransaction | null>(null);

    // Debounced search to avoid too many API calls
    const [debouncedSearch, setDebouncedSearch] = useState<string>('');

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(filters.search);
        }, 300);

        return () => clearTimeout(timer);
    }, [filters.search]);

    useEffect(() => {
        fetchBankTransactions();
    }, [pagination.page, pagination.limit, debouncedSearch, filters.reconciliation_status, filters.direction, filters.transaction_types, advancedFilters, sortBy, sortDir, tagFilter]);

    const fetchBankTransactions = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: pagination.page.toString(),
                limit: pagination.limit.toString(),
            });

            if (filters.reconciliation_status && filters.reconciliation_status !== 'all') {
                params.append('reconciliation_status', filters.reconciliation_status);
            }
            if (filters.direction && filters.direction !== 'all') {
                // Convert UI direction to amount filter
                if (filters.direction === 'incoming') {
                    params.append('min_amount', '0');
                } else if (filters.direction === 'outgoing') {
                    params.append('max_amount', '0');
                }
            }
            if (filters.transaction_types && filters.transaction_types.length > 0) {
                // Send multiple transaction types as separate query params
                filters.transaction_types.forEach(type => {
                    params.append('transaction_types', type);
                });
            }
            if (debouncedSearch) {
                params.append('search', debouncedSearch);
            }

            // Add sorting parameters
            if (sortBy) {
                params.append('sort_by', sortBy);
            }
            if (sortDir) {
                params.append('sort_dir', sortDir);
            }

            // Add advanced filters
            Object.entries(advancedFilters).forEach(([key, value]) => {
                if (value) {
                    params.append(key, value);
                }
            });

            // Add tag filtering
            if (tagFilter && tagFilter.tagIds.length > 0) {
                tagFilter.tagIds.forEach(id => params.append('tag_ids', id));
                params.set('tag_match_mode', tagFilter.matchMode);
            }

            const response = await apiFetch(`/api/payments/bank-transactions/?${params.toString()}`);
            if (response.ok) {
                const data = await response.json();
                setBankTransactions(data.payments || []);
                setPagination(prev => ({
                    ...prev,
                    total: data.total || 0,
                    pages: data.pagination?.pages || 0,
                }));
            } else {
                console.error('API response not ok:', response.status, response.statusText);
                setBankTransactions([]);
            }
        } catch (error) {
            console.error('Error fetching bank transactions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key: keyof Filters, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleAdvancedFiltersChange = (newFilters: AdvancedFilters) => {
        setAdvancedFilters(newFilters);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const clearFilters = () => {
        setFilters({
            reconciliation_status: '',
            direction: '',
            transaction_types: [],
            search: '',
        });
        setAdvancedFilters({});
        setTagFilter(null);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleSort = (column: string) => {
        const newDir: 'asc' | 'desc' = sortBy === column ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
        setSortBy(column);
        setSortDir(newDir);
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    };

    const handleOpenReconcileModal = (transaction: BankTransaction) => {
        setSelectedTransaction(transaction);
        setReconcileModalOpen(true);
    };

    const handleReconcileSuccess = () => {
        setReconcileModalOpen(false);
        setSelectedTransaction(null);
        showSuccessToast('Transaction reconciled successfully');
        fetchBankTransactions(); // Refresh the list
    };

    const handleReconcile = async () => {
        if (!confirm('Run automatic reconciliation on all unreconciled transactions? This will match transactions to invoices based on invoice numbers in the description.')) {
            return;
        }

        setReconciling(true);
        try {
            const response = await apiFetch('/api/payments/bank-transactions/reconcile', {
                method: 'POST',
            });

            if (response.ok) {
                const result = await response.json();
                alert(`Reconciliation complete!\n\nMatched: ${result.matched}\nFailed: ${result.failed}\nTotal: ${result.total_transactions}`);
                // Refresh the list
                fetchBankTransactions();
            } else {
                const error = await response.json();
                alert(`Reconciliation failed: ${error.detail || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error running reconciliation:', error);
            alert('Error running reconciliation. Please try again.');
        } finally {
            setReconciling(false);
        }
    };

    return (
        <CurrentUserProvider>
            <Layout>
                <TooltipProvider>
                    <div className="space-y-6">
                        <div className="flex justify-between items-center">
                            <h1 className="text-3xl font-bold tracking-tight">{t('navigation.bankTransactions', 'common')}</h1>
                            <Button
                                onClick={handleReconcile}
                                disabled={reconciling}
                                variant="default"
                            >
                                {reconciling ? 'Reconciling...' : 'Reconcile Transactions'}
                            </Button>
                        </div>

                        <div className="flex flex-wrap gap-4 mb-4">
                            <div className="relative flex-1 min-w-[200px]">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                                <Input
                                    placeholder="Search by reference, description..."
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                    className="pl-10"
                                />
                            </div>

                            <Select
                                value={filters.reconciliation_status || 'all'}
                                onValueChange={(value) => handleFilterChange('reconciliation_status', value)}
                            >
                                <SelectTrigger className="w-36">
                                    <SelectValue placeholder="Filter by status" />
                                </SelectTrigger>
                                <SelectContent>
                                    {reconciliationStatusOptions.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                            <Select
                                value={filters.direction || 'all'}
                                onValueChange={(value) => handleFilterChange('direction', value)}
                            >
                                <SelectTrigger className="w-36">
                                    <SelectValue placeholder="Filter by direction" />
                                </SelectTrigger>
                                <SelectContent>
                                    {directionOptions.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                            <MultipleSelector
                                value={filters.transaction_types.map(type => ({ value: type, label: type }))}
                                onChange={(options: Option[]) => {
                                    setFilters(prev => ({ ...prev, transaction_types: options.map(o => o.value) }));
                                    setPagination(prev => ({ ...prev, page: 1 }));
                                }}
                                options={transactionTypeOptions.filter(opt => opt.value !== 'all').map(opt => ({ value: opt.value, label: opt.label }))}
                                placeholder="Filter by type"
                                className="w-96"
                                hidePlaceholderWhenSelected
                            />

                            <TagFilter
                                entityType="payments"
                                value={tagFilter}
                                onFilterChange={(selection) => {
                                    setTagFilter(selection);
                                    // Reset to page 1 when tag filter changes
                                    setPagination(prev => ({ ...prev, page: 1 }));
                                }}
                            />

                            <Button variant="outline" onClick={clearFilters}>
                                {t('actions.clearFilters', 'common')}
                            </Button>
                        </div>

                        <PaymentAdvancedFilter
                            filters={advancedFilters}
                            onFiltersChange={handleAdvancedFiltersChange}
                            onClear={() => setAdvancedFilters({})}
                            hideVendorFilter={true}
                        />


                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead onClick={() => handleSort('transaction_date')} className="cursor-pointer select-none">
                                            <span className="inline-flex items-center gap-1">
                                                Date
                                                {sortBy === 'transaction_date' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                            </span>
                                        </TableHead>
                                        <TableHead onClick={() => handleSort('payment_instrument_name')} className="cursor-pointer select-none">
                                            <span className="inline-flex items-center gap-1">
                                                Payment Instrument
                                                {sortBy === 'payment_instrument_name' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                            </span>
                                        </TableHead>
                                        <TableHead onClick={() => handleSort('transaction_type')} className="cursor-pointer select-none">
                                            <span className="inline-flex items-center gap-1">
                                                Type
                                                {sortBy === 'transaction_type' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                            </span>
                                        </TableHead>
                                        <TableHead onClick={() => handleSort('reference')} className="cursor-pointer select-none">
                                            <span className="inline-flex items-center gap-1">
                                                Reference
                                                {sortBy === 'reference' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                            </span>
                                        </TableHead>
                                        <TableHead onClick={() => handleSort('description')} className="cursor-pointer select-none">
                                            <span className="inline-flex items-center gap-1">
                                                Description
                                                {sortBy === 'description' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                            </span>
                                        </TableHead>
                                        <TableHead onClick={() => handleSort('amount')} className="cursor-pointer select-none text-right">
                                            <span className="inline-flex items-center gap-1">
                                                Amount
                                                {sortBy === 'amount' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                            </span>
                                        </TableHead>
                                        <TableHead onClick={() => handleSort('reconciliation_status')} className="cursor-pointer select-none">
                                            <span className="inline-flex items-center gap-1">
                                                Status
                                                {sortBy === 'reconciliation_status' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                            </span>
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={8} className="text-center py-8">
                                                Loading...
                                            </TableCell>
                                        </TableRow>
                                    ) : bankTransactions.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                                                No bank transactions found
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        bankTransactions.map((tx) => {
                                            return (
                                                <TableRow
                                                    key={tx.id}
                                                    className="cursor-pointer hover:bg-muted/50"
                                                >
                                                    <TableCell>
                                                        {formatDate(tx.transaction_date)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {tx.payment_instrument?.name || '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline">{tx.transaction_type || 'N/A'}</Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        {tx.reconciliation_status === 'unreconciled' ? (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => handleOpenReconcileModal(tx)}
                                                            >
                                                                Reconcile
                                                            </Button>
                                                        ) : tx.invoices && tx.invoices.length > 1 ? (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <span className="cursor-help">Multiple</span>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    <div className="space-y-1">
                                                                        {tx.invoices.map((inv) => (
                                                                            <div key={inv.id}>{inv.invoice_number || 'N/A'}</div>
                                                                        ))}
                                                                    </div>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        ) : (
                                                            tx.invoices?.[0]?.invoice_number || tx.invoice?.invoice_number || tx.bank_reference || '-'
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="max-w-xs">
                                                        {tx.description ? (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <div className="truncate">
                                                                        {tx.description}
                                                                    </div>
                                                                </TooltipTrigger>
                                                                <TooltipContent className="max-w-md">
                                                                    <p>{tx.description}</p>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        ) : '-'}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <span className={tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                                                            {formatCurrency(tx.amount, tx.currency)}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant={getReconciliationStatusVariant(tx.reconciliation_status)}>
                                                            {tx.reconciliation_status}
                                                        </Badge>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </div>


                    {/* Pagination */}
                    <PaginationFooter
                        pagination={pagination}
                        onPageChange={(newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
                        onPageSizeChange={(pageSize) => {
                            setPagination(prev => ({ ...prev, limit: pageSize, page: 1 }));
                        }}
                    />
                </div>
            </TooltipProvider>

            {/* Reconcile Modal */}
            {selectedTransaction && (
                <BankReconcileModal
                    isOpen={reconcileModalOpen}
                    onClose={() => {
                        setReconcileModalOpen(false);
                        setSelectedTransaction(null);
                    }}
                    transaction={selectedTransaction}
                    onReconcileSuccess={handleReconcileSuccess}
                />
            )}
        </Layout>
    </CurrentUserProvider>
    );
}

export const Route = createFileRoute('/bank-transactions/')({
  validateSearch: bankTransactionsSearchSchema,
  component: BankTransactionsPage,
})
